import { useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { MentionedText } from "@/components/AtMention";
import Icon from "@/components/Icon";
import ListItem from "@/components/ListItem/ListItem";
import ObjectHeading from "@/components/ObjectHeading";
import SubSectionHeading from "@/components/SubSectionHeading";
import Tag from "@/components/Tag";
import {
  getIcon,
  getInteractionDisplayName,
  getInteractionParticipantHeading,
  getInteractionParticipantNamesLabel,
  InteractionDescriptionContent,
  type Interaction,
  type InteractionMode
} from "@/components/interactions";
import type { MaterialNames } from "@/components/material_symbol_names";
import { useCanViewTasksBool } from "@/hooks/useCapabilities";
import { toDateTimeFormat, toFormat, toUTCFormat } from "@/lib/dateHelpers";
import { PERSON_NOTES_QUERY, type PersonNotesQueryResponse } from "@/pages/people/[pgaId]/person/notes/queries";
import { TASKS_QUERY, type TasksQueryResponse, type TasksQueryVariables } from "@/pages/tasks/queries";
import { NoteNotableValues, type CRMNote, type CRMTask } from "@/types";

export interface CustomActionButton {
  text: string;
  variant?: "text" | "outlined" | "filled";
  onClick: () => void;
  testId?: string;
  iconName?: MaterialNames;
}

type InteractionSideSheetContentProps = {
  interaction: Interaction;
  pgaId: string | null | undefined;
  customActionButtons?: CustomActionButton[];
};

function safeGetIcon(mode: InteractionMode | null): MaterialNames {
  return mode ? getIcon(mode) : "record_voice_over";
}

function getInteractionWithLabel(interaction: Interaction, pgaId: string | null | undefined) {
  const participants = interaction.participants ?? [];

  if (participants.length > 1) {
    return getInteractionParticipantHeading(participants, interaction.relatedTo);
  }

  const currentParticipant = participants.find((participant) => participant.pgaId === pgaId);
  if (currentParticipant) {
    return `${currentParticipant.firstName} ${currentParticipant.lastName}`;
  }

  return getInteractionParticipantHeading(participants, interaction.relatedTo);
}

function getJobPostSupportingText(jobPost: Interaction["jobPost"]) {
  if (!jobPost) return "--";

  const jobPostWithCategory = jobPost as Interaction["jobPost"] & { category?: { name?: string | null } };

  if (jobPostWithCategory.category?.name) {
    return jobPostWithCategory.category.name;
  }

  if (jobPost.facility?.name) {
    return jobPost.facility.name;
  }

  return "--";
}

function canReadLastMessage(interaction: Interaction) {
  return !interaction.lastMessage.isPrivate || interaction.lastMessage.haveAccess;
}

function formatInteractionDate(date: Interaction["interactionDate"]) {
  return toUTCFormat(date instanceof Date ? date.toISOString() : String(date));
}

export default function InteractionSideSheetContent({
  interaction,
  pgaId,
  customActionButtons
}: InteractionSideSheetContentProps) {
  const navigate = useNavigate();
  const mode = interaction.mode || null;
  const withLabel = getInteractionWithLabel(interaction, pgaId);
  const participantNamesLabel = getInteractionParticipantNamesLabel(interaction.participants);
  const canViewTasks = useCanViewTasksBool();
  const showTasks = canViewTasks;
  const canReadMessage = canReadLastMessage(interaction);
  const topicText = interaction.categories?.length ? interaction.categories.join(", ") : "--";
  const formattedInteractionDate = interaction.interactionDate
    ? formatInteractionDate(interaction.interactionDate)
    : null;
  const formattedModifiedDate = interaction.lastModifiedDate
    ? toDateTimeFormat(String(interaction.lastModifiedDate))
    : null;

  const { data: notesData } = useQuery<PersonNotesQueryResponse>(PERSON_NOTES_QUERY, {
    variables: {
      pgaId: pgaId || "",
      first: 25,
      input: { notableType: [NoteNotableValues.INTERACTION], notableId: interaction.id }
    },
    skip: !pgaId || !interaction.id
  });

  const { data: tasksData } = useQuery<TasksQueryResponse, TasksQueryVariables>(TASKS_QUERY, {
    variables: {
      first: 25,
      input: {
        referenceableId: interaction.id,
        referenceableType: ["INTERACTION"],
        stage: ["OPEN"]
      }
    },
    skip: !showTasks || (interaction.openTasksCount ?? 0) === 0
  });

  const interactionNotes = notesData?.personNotes?.nodes ?? [];
  const openTasks = (tasksData?.tasks?.nodes ?? []).filter(Boolean);
  const isPrivateToDepartment = interaction.lastMessage.isPrivate && !interaction.lastMessage.haveAccess;
  const privacyValue = interaction.lastMessage.isPrivate ? "Private to Department" : "Public";

  return (
    <div>
      <ObjectHeading
        title={getInteractionDisplayName(interaction, withLabel)}
        iconName={safeGetIcon(mode)}
        variant="condensed"
        metaData={participantNamesLabel ? [{ text: participantNamesLabel }] : undefined}
        actions={customActionButtons?.map((btn) => ({
          text: btn.text,
          variant: btn.variant,
          onClick: btn.onClick,
          testId: btn.testId,
          iconName: btn.iconName
        }))}
        tag={
          isPrivateToDepartment ? (
            <Tag tagType="error" iconName="lock" title="Other departments cannot access this interaction">
              <Icon name="lock" size={16} className="mr-1" />
              Private to Department
            </Tag>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-2 items-start mt-8">
        <SubSectionHeading>Overview</SubSectionHeading>
        <div className="space-y-4 w-full">
          <ListItem iconName="topic" iconBackground overline="Topic" headline={topicText} />
          <ListItem
            iconName="description"
            iconBackground
            overline="Message"
            headline={
              canReadMessage ? (
                <InteractionDescriptionContent content={interaction.lastMessage.content} />
              ) : (
                "You don't have access to this message"
              )
            }
            longContent
          />
        </div>
      </div>

      <div className="flex flex-col gap-2 items-start mt-8">
        <SubSectionHeading>Details</SubSectionHeading>
        <div className="space-y-4 w-full">
          <ListItem iconName={safeGetIcon(mode)} iconBackground overline="Method" headline={mode || "--"} />
          <ListItem
            iconName={interaction.lastMessage.isPrivate ? "lock" : "visibility"}
            iconBackground
            overline="Privacy"
            headline={privacyValue}
          />
          <ListItem
            iconName="person"
            iconBackground
            overline="Owner"
            headline={interaction.owner?.name || "--"}
            supportingText={interaction.owner?.email || null}
            copyable={!!interaction.owner?.email}
            copyText={interaction.owner?.email || undefined}
          />
          {formattedInteractionDate && (
            <ListItem
              iconName="calendar_today"
              iconBackground
              overline="Occurred"
              headline={formattedInteractionDate}
            />
          )}
          {formattedModifiedDate && (
            <ListItem iconName="history" iconBackground overline="Modified" headline={formattedModifiedDate} />
          )}
        </div>
      </div>

      {interaction.jobPost && (
        <div className="flex flex-col gap-2 items-start mt-8">
          <SubSectionHeading>Associated Items</SubSectionHeading>
          <div className="space-y-4 w-full">
            <ListItem
              iconName="business_center"
              iconBackground
              overline="Job"
              headline={interaction.jobPost.name || "--"}
              supportingText={getJobPostSupportingText(interaction.jobPost)}
              action={() => navigate(`/jobs/job-post/${interaction.jobPost?.id}`)}
            />
          </div>
        </div>
      )}

      {interactionNotes.length > 0 && (
        <div className="flex flex-col gap-2 items-start mt-8">
          <SubSectionHeading>Notes</SubSectionHeading>
          <div className="space-y-4 w-full">
            {interactionNotes.map((note: CRMNote) => (
              <ListItem
                key={note.id}
                iconName="sticky_note_2"
                iconBackground
                headline={note.displayTitle || note.title || "Untitled Note"}
                supportingText={note.content || null}
                longContent={!!note.content}
                action={() =>
                  navigate(`/people/${pgaId}/interactions/interactions/${interaction.id}?highlightNote=${note.id}`)
                }
              />
            ))}
          </div>
        </div>
      )}

      {showTasks && openTasks.length > 0 && (
        <div className="flex flex-col gap-2 items-start mt-8" data-testid="interaction-open-tasks-section">
          <SubSectionHeading badgeCount={openTasks.length}>Open Tasks</SubSectionHeading>
          <div className="space-y-4 w-full">
            {openTasks.map((task: CRMTask) => (
              <ListItem
                key={task.id}
                iconName="inventory"
                iconBackground
                headline={<MentionedText content={task.name || "--"} />}
                supportingText={
                  [
                    task.dueDate ? `Due: ${toFormat(task.dueDate, "MMM dd, yyyy")}` : null,
                    task.assignedTo?.name ? `Assignee: ${task.assignedTo.name}` : null
                  ]
                    .filter(Boolean)
                    .join("\n") || null
                }
                action={() => navigate(`/tasks/${task.id}?highlightReference=${interaction.id}`)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
